import { useState } from 'react';
import { api } from '../api/client';

export type UploadPurpose = 'chat' | 'profile';

interface UseImageUploadOptions {
  purpose?: UploadPurpose;
  onSuccess?: (url: string) => void;
  onError?: (message: string) => void;
}

export function useImageUpload({ purpose = 'chat', onSuccess, onError }: UseImageUploadOptions = {}) {
  const [uploading, setUploading] = useState(false);
  const [pendingUri, setPendingUri] = useState<string | null>(null);

  const compressImageFile = async (file: File): Promise<Blob> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          const maxDim = 1000;

          if (width > maxDim || height > maxDim) {
            if (width > height) {
              height = Math.round((height * maxDim) / width);
              width = maxDim;
            } else {
              width = Math.round((width * maxDim) / height);
              height = maxDim;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0, width, height);
            canvas.toBlob(
              (blob) => {
                resolve(blob || file);
              },
              'image/jpeg',
              0.75
            );
          } else {
            resolve(file);
          }
        };
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    });
  };

  const uploadFile = async (file: File): Promise<string | null> => {
    setUploading(true);
    const objectUrl = URL.createObjectURL(file);
    setPendingUri(objectUrl);

    try {
      const compressedBlob = await compressImageFile(file);
      const formData = new FormData();
      formData.append('image', compressedBlob, file.name || 'upload.jpg');

      const endpoint = purpose === 'profile' ? 'profile' : undefined;
      const res = await api.upload.image(formData, endpoint);

      if (res.success && res.data?.url) {
        onSuccess?.(res.data.url);
        return res.data.url;
      } else {
        throw new Error(res.message || 'Upload failed');
      }
    } catch (err: any) {
      const msg = err.message || 'Failed to upload image';
      onError?.(msg);
      return null;
    } finally {
      setUploading(false);
    }
  };

  const showPicker = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e: any) => {
      const file = e.target?.files?.[0];
      if (file) {
        uploadFile(file);
      }
    };
    input.click();
  };

  return {
    uploading,
    pendingUri,
    showPicker,
    uploadFile,
  };
}
